import { FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { classNames, formatCLP } from '../lib/format';
import type { Promotion } from '../types';

const emptyPromo: Partial<Promotion> = {
  name: '',
  type: 'coupon',
  status: 'draft',
  description: '',
  startsAt: null,
  endsAt: null,
  discountType: 'percent',
  discountValue: 10,
  couponCode: '',
  minOrder: 0,
  usageLimit: 0,
  usageCount: 0
};

export function PromotionsPanel({ advanced }: { advanced: boolean }) {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [editing, setEditing] = useState<Partial<Promotion>>(emptyPromo);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  async function load() {
    setLoading(true);
    try {
      setPromotions(await api.admin.promotions());
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Error cargando promociones' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function reset() {
    setEditing(emptyPromo);
    setMessage(null);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const saved = await api.admin.savePromotion(editing);
      setEditing(saved);
      setMessage({ tone: 'ok', text: '✓ Promoción guardada.' });
      await load();
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'No se pudo guardar' });
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string, name: string) {
    if (!window.confirm(`¿Borrar la promoción "${name}"? No afecta pedidos ya emitidos.`)) return;
    await api.admin.deletePromotion(id);
    if (editing.id === id) reset();
    await load();
  }

  return (
    <div className="admin-two-column">
      <section className="admin-card">
        <div className="admin-card-head">
          <h2>Promociones y cupones</h2>
          <button className="ghost-button compact" onClick={reset}>+ Nueva</button>
        </div>
        {loading ? (
          <p className="muted">Cargando...</p>
        ) : promotions.length === 0 ? (
          <div className="empty-state subtle">
            <p>Aún no hay promociones.</p>
            <p className="muted">Crea la primera con el botón "Nueva". Sirve para cupones de descuento, envío gratis o vitrinas destacadas.</p>
          </div>
        ) : (
          <ul className="promo-list">
            {promotions.map((promo) => (
              <li key={promo.id} className={classNames('promo-list-row', editing.id === promo.id && 'active')}>
                <button type="button" className="promo-row-main" onClick={() => { setEditing(promo); setMessage(null); }}>
                  <div className="promo-row-meta">
                    <strong>{promo.name}</strong>
                    <small>
                      {promo.type === 'coupon' && promo.couponCode && <code>{promo.couponCode}</code>}
                      {promo.type === 'free_shipping' && 'Envío gratis'}
                      {promo.type === 'discount' && `-${promo.discountValue}${promo.discountType === 'percent' ? '%' : ' CLP'}`}
                      {promo.type === 'collection' && 'Vitrina/colección'}
                      {promo.type === 'banner' && 'Banner'}
                    </small>
                  </div>
                  <span className={classNames('promo-status', `status-${promo.status}`)}>{promo.status}</span>
                </button>
                {advanced && (
                  <button type="button" className="promo-row-delete" onClick={() => remove(promo.id, promo.name)} title="Eliminar">×</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <form className="admin-card" onSubmit={save}>
        <div className="admin-card-head">
          <h2>{editing.id ? 'Editar promoción' : 'Nueva promoción'}</h2>
          <button className="primary-button compact" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
        </div>

        <div className="form-grid">
          <label className="span-2">
            Nombre interno
            <input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ej: Lanzamiento abril -20%" required />
          </label>

          <label>
            Tipo
            <select value={editing.type || 'coupon'} onChange={(e) => setEditing({ ...editing, type: e.target.value as Promotion['type'] })}>
              <option value="coupon">Cupón con código</option>
              <option value="discount">Descuento automático</option>
              <option value="free_shipping">Envío gratis</option>
              <option value="collection">Vitrina destacada</option>
              <option value="banner">Banner home</option>
            </select>
          </label>

          <label>
            Estado
            <select value={editing.status || 'draft'} onChange={(e) => setEditing({ ...editing, status: e.target.value as Promotion['status'] })}>
              <option value="draft">Borrador</option>
              <option value="active">Activa</option>
              <option value="paused">Pausada</option>
              <option value="expired">Expirada</option>
            </select>
          </label>

          {(editing.type === 'coupon' || editing.type === 'discount' || editing.type === 'free_shipping') && (
            <>
              {editing.type === 'coupon' && (
                <label>
                  Código del cupón
                  <input value={editing.couponCode || ''} onChange={(e) => setEditing({ ...editing, couponCode: e.target.value.toUpperCase() })} placeholder="VERANO20" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }} />
                </label>
              )}

              {editing.type !== 'free_shipping' && (
                <>
                  <label>
                    Tipo de descuento
                    <select value={editing.discountType || 'percent'} onChange={(e) => setEditing({ ...editing, discountType: e.target.value as Promotion['discountType'] })}>
                      <option value="percent">Porcentaje (%)</option>
                      <option value="fixed">Monto fijo (CLP)</option>
                    </select>
                  </label>

                  <label>
                    Valor
                    <input type="number" min={0} value={editing.discountValue ?? 0} onChange={(e) => setEditing({ ...editing, discountValue: Number(e.target.value) })} placeholder={editing.discountType === 'percent' ? '20' : '5000'} />
                  </label>
                </>
              )}

              {advanced && (
                <>
                  <label>
                    Pedido mínimo (CLP)
                    <input type="number" min={0} value={editing.minOrder ?? 0} onChange={(e) => setEditing({ ...editing, minOrder: Number(e.target.value) })} placeholder="0 = sin mínimo" />
                  </label>

                  <label>
                    Límite de usos
                    <input type="number" min={0} value={editing.usageLimit ?? 0} onChange={(e) => setEditing({ ...editing, usageLimit: Number(e.target.value) })} placeholder="0 = ilimitado" />
                  </label>

                  <label>
                    Inicia (opcional)
                    <input type="date" value={(editing.startsAt || '').slice(0, 10)} onChange={(e) => setEditing({ ...editing, startsAt: e.target.value || null })} />
                  </label>

                  <label>
                    Termina (opcional)
                    <input type="date" value={(editing.endsAt || '').slice(0, 10)} onChange={(e) => setEditing({ ...editing, endsAt: e.target.value || null })} />
                  </label>
                </>
              )}
            </>
          )}

          <label className="span-2">
            Descripción (opcional, se muestra al cliente cuando aplica el cupón)
            <textarea rows={2} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Ej: 20% off válido en toda la tienda hasta el 30 de abril" />
          </label>
        </div>

        {editing.id && editing.usageCount && editing.usageCount > 0 ? (
          <p className="muted small">Usos hasta ahora: <strong>{editing.usageCount}</strong>{editing.usageLimit ? ` de ${editing.usageLimit}` : ''}.</p>
        ) : null}

        {message && <p className={classNames('form-message', `tone-${message.tone}`)}>{message.text}</p>}
      </form>
    </div>
  );
}

export function CouponInput({ onApplied, onCleared, subtotal }: { onApplied: (info: { code: string; discount: number; freeShipping: boolean; description?: string }) => void; onCleared: () => void; subtotal: number }) {
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  async function check() {
    if (!code.trim()) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await api.checkCoupon(code.trim());
      if (!res.valid) {
        setMessage(res.message || 'Cupón no válido');
        return;
      }
      if (res.minOrder && subtotal < res.minOrder) {
        setMessage(`Este cupón requiere un pedido mínimo de ${formatCLP(res.minOrder)}`);
        return;
      }
      const discount = res.discountType === 'percent'
        ? Math.round((subtotal * (res.discountValue || 0)) / 100)
        : Math.min(subtotal, res.discountValue || 0);
      const freeShipping = res.type === 'free_shipping';
      setApplied(res.code);
      onApplied({ code: res.code, discount, freeShipping, description: res.description });
      setMessage(null);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'No se pudo validar el cupón');
    } finally {
      setBusy(false);
    }
  }

  function clear() {
    setApplied(null);
    setCode('');
    setMessage(null);
    onCleared();
  }

  if (applied) {
    return (
      <div className="coupon-applied">
        <span><strong>{applied}</strong> aplicado</span>
        <button type="button" className="link-button" onClick={clear}>Quitar</button>
      </div>
    );
  }

  if (!open) {
    return (
      <button type="button" className="coupon-toggle" onClick={() => setOpen(true)}>+ ¿Tienes un código de descuento?</button>
    );
  }

  return (
    <div className="coupon-block">
      <div className="coupon-row">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CODIGO" style={{ textTransform: 'uppercase' }} />
        <button type="button" className="ghost-button compact" onClick={check} disabled={busy}>{busy ? 'Validando...' : 'Aplicar'}</button>
      </div>
      {message && <p className="muted small" style={{ color: '#a02020' }}>{message}</p>}
    </div>
  );
}
